/**
 * RTWO Explorer Platform — Domain Models
 * Phase 15 | Module ID: RTWO-EXPL-001
 * Version: 1.0.0-phase15
 *
 * Strongly typed domain models for the RTWO Explorer Platform.
 * Defines the blockchain visibility layer for RTWO Chain.
 *
 * Truthful Data Policy:
 * - No fake blocks, transactions, addresses, balances, assets, contracts, or search results.
 * - Explorer is NOT claimed as live or publicly deployed.
 * - All data must originate from the Indexer Engine connected to real blockchain data.
 * - explorer.rtwo.network is NOT operational.
 *
 * Source: Explorer Platform (RTWO-EXPL-001)
 * Data Flow: Indexer Engine → Event Bus → Explorer Platform → Explorer UI
 */

// ─────────────────────────────────────────────
// PRIMITIVE ID TYPES
// ─────────────────────────────────────────────

export type ViewId = string;
export type QueryId = string;
export type DataSourceId = string;
export type PolicyId = string;
export type EXPLTimestamp = number | null;

export const EXPL_ENGINE_VERSION = {
  moduleId: 'RTWO-EXPL-001',
  moduleName: 'Explorer Platform',
  version: '1.0.0-phase15',
  phase: 'Phase 15 — Explorer Platform',
  status: 'Structure Complete — Waiting for Indexed Blockchain Data',
  productionReadiness: 'Pending Indexer Data and Explorer Backend',
  policy:
    'Truthful Data Policy Enforced — No Fake Blocks, Transactions, Addresses, Balances, Assets, Contracts, or Search Results',
  dataFlow: 'Indexer Engine → Event Bus → Explorer Platform → Explorer UI',
} as const;

// ─────────────────────────────────────────────
// EXPLORER STATUS TYPES
// ─────────────────────────────────────────────

export type RTWOExplorerStatus =
  | 'explorer_architecture_ready'
  | 'waiting_for_indexed_blockchain_data'
  | 'no_blocks_indexed'
  | 'no_transactions_indexed'
  | 'no_address_data'
  | 'no_asset_data'
  | 'no_contract_data'
  | 'indexer_source_not_connected'
  | 'explorer_not_deployed'
  | 'structure_complete'
  | 'production_pending';

export const EXPLORER_STATUS_LABELS: Record<RTWOExplorerStatus, string> = {
  explorer_architecture_ready: 'Explorer Architecture Ready',
  waiting_for_indexed_blockchain_data: 'Waiting for Indexed Blockchain Data',
  no_blocks_indexed: 'No Blocks Indexed',
  no_transactions_indexed: 'No Transactions Indexed',
  no_address_data: 'No Address Data',
  no_asset_data: 'No Asset Data',
  no_contract_data: 'No Contract Data',
  indexer_source_not_connected: 'Indexer Source Not Connected',
  explorer_not_deployed: 'Explorer Not Deployed',
  structure_complete: 'Structure Complete',
  production_pending: 'Production Pending',
};

// ─────────────────────────────────────────────
// VIEW STATUS TYPES
// ─────────────────────────────────────────────

export type RTWOExplorerViewStatus =
  | 'architecture_ready'
  | 'waiting_for_indexer_data'
  | 'indexer_not_connected'
  | 'no_data'
  | 'populated'
  | 'stale'
  | 'error';

export const EXPLORER_VIEW_STATUS_LABELS: Record<RTWOExplorerViewStatus, string> = {
  architecture_ready: 'Architecture Ready',
  waiting_for_indexer_data: 'Waiting for Indexer Data',
  indexer_not_connected: 'Indexer Not Connected',
  no_data: 'No Data',
  populated: 'Populated',
  stale: 'Stale',
  error: 'Error',
};

// ─────────────────────────────────────────────
// DOMAIN MODEL 1: RTWOExplorerBlockView
// ─────────────────────────────────────────────

export interface RTWOExplorerBlockView {
  viewId: ViewId;
  blockReference: string;
  blockNumberReference: string;
  blockHashReference: string;
  transactionCountReference: string;
  proposerReference: string;
  timestampReference: string;
  sourceReference: string;
  status: RTWOExplorerViewStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 2: RTWOExplorerTransactionView
// ─────────────────────────────────────────────

export interface RTWOExplorerTransactionView {
  viewId: ViewId;
  transactionReference: string;
  transactionHashReference: string;
  fromAddressReference: string;
  toAddressReference: string;
  amountReference: string;
  feeReference: string;
  blockReference: string;
  sourceReference: string;
  status: RTWOExplorerViewStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 3: RTWOExplorerAddressView
// ─────────────────────────────────────────────

export interface RTWOExplorerAddressView {
  viewId: ViewId;
  addressReference: string;
  balanceReference: string;
  transactionCountReference: string;
  assetHoldingsReference: string;
  sourceReference: string;
  status: RTWOExplorerViewStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 4: RTWOExplorerAssetView
// ─────────────────────────────────────────────

export interface RTWOExplorerAssetView {
  viewId: ViewId;
  assetReference: string;
  symbolReference: string;
  holderCountReference: string;
  supplyReference: string;
  transferCountReference: string;
  sourceReference: string;
  status: RTWOExplorerViewStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 5: RTWOExplorerContractView
// ─────────────────────────────────────────────

export interface RTWOExplorerContractView {
  viewId: ViewId;
  contractReference: string;
  contractAddressReference: string;
  deploymentReference: string;
  executionReference: string;
  eventReference: string;
  sourceReference: string;
  status: RTWOExplorerViewStatus;
}

// ─────────────────────────────────────────────
// QUERY TYPES
// ─────────────────────────────────────────────

export type RTWOExplorerQueryType =
  | 'block_number'
  | 'block_hash'
  | 'transaction_hash'
  | 'address'
  | 'asset_symbol'
  | 'contract_address'
  | 'full_text'
  | 'not_configured';

export type RTWOExplorerQueryStatus =
  'architecture_ready' | 'indexer_not_connected' | 'no_results' | 'results_found' | 'error';

// ─────────────────────────────────────────────
// DOMAIN MODEL 6: RTWOExplorerSearchQuery
// ─────────────────────────────────────────────

export interface RTWOExplorerSearchQuery {
  queryId: QueryId;
  queryText: string;
  queryType: RTWOExplorerQueryType;
  sourceReference: string;
  status: RTWOExplorerQueryStatus;
  createdAt: EXPLTimestamp;
}

// ─────────────────────────────────────────────
// DATA SOURCE TYPES
// ─────────────────────────────────────────────

export type RTWOExplorerDataSourceType =
  | 'indexer_engine'
  | 'event_bus'
  | 'explorer_backend'
  | 'rpc_platform'
  | 'ledger_engine'
  | 'asset_engine'
  | 'contract_runtime'
  | 'not_configured';

export type RTWOExplorerConnectionStatus =
  | 'not_connected'
  | 'not_configured'
  | 'not_deployed'
  | 'waiting_for_real_data'
  | 'connected'
  | 'error';

export const EXPLORER_CONNECTION_STATUS_LABELS: Record<RTWOExplorerConnectionStatus, string> = {
  not_connected: 'Not Connected',
  not_configured: 'Not Configured',
  not_deployed: 'Not Deployed',
  waiting_for_real_data: 'Waiting for Real Data',
  connected: 'Connected',
  error: 'Error',
};

// ─────────────────────────────────────────────
// DOMAIN MODEL 7: RTWOExplorerDataSource
// ─────────────────────────────────────────────

export interface RTWOExplorerDataSource {
  dataSourceId: DataSourceId;
  sourceType: RTWOExplorerDataSourceType;
  sourceName: string;
  connectionStatus: RTWOExplorerConnectionStatus;
  lastCheckedAt: EXPLTimestamp;
}

// ─────────────────────────────────────────────
// VIEW POLICY TYPES
// ─────────────────────────────────────────────

export type RTWOExplorerViewType =
  | 'block_view'
  | 'transaction_view'
  | 'address_view'
  | 'asset_view'
  | 'contract_view'
  | 'search_view'
  | 'overview_view';

export type RTWOExplorerVisibilityPolicy =
  'public' | 'authenticated' | 'enterprise_only' | 'not_configured';

export type RTWOExplorerRefreshPolicy = 'real_time' | 'polling' | 'on_demand' | 'not_configured';

export type RTWOExplorerViewPolicyStatus =
  'architecture_ready' | 'not_configured' | 'active' | 'suspended';

// ─────────────────────────────────────────────
// DOMAIN MODEL 8: RTWOExplorerViewPolicy
// ─────────────────────────────────────────────

export interface RTWOExplorerViewPolicy {
  policyId: PolicyId;
  viewType: RTWOExplorerViewType;
  visibilityPolicy: RTWOExplorerVisibilityPolicy;
  refreshPolicy: RTWOExplorerRefreshPolicy;
  status: RTWOExplorerViewPolicyStatus;
}

// ─────────────────────────────────────────────
// ARCHITECTURE-DEFINED DATA SOURCES
// ─────────────────────────────────────────────

export const RTWO_EXPLORER_DATA_SOURCES: RTWOExplorerDataSource[] = [
  {
    dataSourceId: 'expl-src-001',
    sourceType: 'indexer_engine',
    sourceName: 'Indexer Engine — Waiting for Real Blockchain Data',
    connectionStatus: 'not_connected',
    lastCheckedAt: null,
  },
  {
    dataSourceId: 'expl-src-002',
    sourceType: 'event_bus',
    sourceName: 'Event Bus — Waiting for Real Events',
    connectionStatus: 'not_connected',
    lastCheckedAt: null,
  },
  {
    dataSourceId: 'expl-src-003',
    sourceType: 'explorer_backend',
    sourceName: 'Explorer Backend — Not Deployed',
    connectionStatus: 'not_deployed',
    lastCheckedAt: null,
  },
  {
    dataSourceId: 'expl-src-004',
    sourceType: 'rpc_platform',
    sourceName: 'RPC Platform — Not Connected',
    connectionStatus: 'not_connected',
    lastCheckedAt: null,
  },
  {
    dataSourceId: 'expl-src-005',
    sourceType: 'ledger_engine',
    sourceName: 'Ledger Engine — Waiting for Real Data',
    connectionStatus: 'not_connected',
    lastCheckedAt: null,
  },
  {
    dataSourceId: 'expl-src-006',
    sourceType: 'asset_engine',
    sourceName: 'Asset & Token Engine — Waiting for Real Data',
    connectionStatus: 'not_connected',
    lastCheckedAt: null,
  },
  {
    dataSourceId: 'expl-src-007',
    sourceType: 'contract_runtime',
    sourceName: 'Smart Contract Runtime — Not Operational',
    connectionStatus: 'not_configured',
    lastCheckedAt: null,
  },
];

// ─────────────────────────────────────────────
// ARCHITECTURE-DEFINED VIEW POLICIES
// ─────────────────────────────────────────────

export const RTWO_EXPLORER_VIEW_POLICIES: RTWOExplorerViewPolicy[] = [
  {
    policyId: 'expl-pol-001',
    viewType: 'overview_view',
    visibilityPolicy: 'public',
    refreshPolicy: 'not_configured',
    status: 'architecture_ready',
  },
  {
    policyId: 'expl-pol-002',
    viewType: 'block_view',
    visibilityPolicy: 'public',
    refreshPolicy: 'not_configured',
    status: 'architecture_ready',
  },
  {
    policyId: 'expl-pol-003',
    viewType: 'transaction_view',
    visibilityPolicy: 'public',
    refreshPolicy: 'not_configured',
    status: 'architecture_ready',
  },
  {
    policyId: 'expl-pol-004',
    viewType: 'address_view',
    visibilityPolicy: 'public',
    refreshPolicy: 'not_configured',
    status: 'architecture_ready',
  },
  {
    policyId: 'expl-pol-005',
    viewType: 'asset_view',
    visibilityPolicy: 'public',
    refreshPolicy: 'not_configured',
    status: 'architecture_ready',
  },
  {
    policyId: 'expl-pol-006',
    viewType: 'contract_view',
    visibilityPolicy: 'public',
    refreshPolicy: 'not_configured',
    status: 'architecture_ready',
  },
  {
    policyId: 'expl-pol-007',
    viewType: 'search_view',
    visibilityPolicy: 'public',
    refreshPolicy: 'not_configured',
    status: 'architecture_ready',
  },
];

// ─────────────────────────────────────────────
// EVENT TYPES
// ─────────────────────────────────────────────

export type EXPLEventType =
  | 'explorer_initialized'
  | 'data_source_connected'
  | 'data_source_disconnected'
  | 'block_view_updated'
  | 'transaction_view_updated'
  | 'address_view_updated'
  | 'asset_view_updated'
  | 'contract_view_updated'
  | 'search_query_executed'
  | 'indexer_data_received'
  | 'event_bus_event_received';

export type EXPLEventHandler = (event: { eventType: EXPLEventType; payload: unknown }) => void;
